import React from 'react';
import { useQuery } from '@apollo/client';
import { useParams, Link } from 'react-router-dom';
import { COMPANY_QUERY } from '../graphql';
import { useAuth } from '../context/AuthContext';

export default function CompanyProfile() {
  const { slug } = useParams();
  const { currentUser } = useAuth();

  const { data, loading, error } = useQuery(COMPANY_QUERY, {
    variables: { slug },
    skip: !slug,
  });

  if (loading) {
    return (
      <div className="page container" style={{ paddingTop: 80, textAlign: 'center' }}>
        <div style={{ color: 'var(--text-muted)' }}>Loading company profile…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page container" style={{ paddingTop: 80 }}>
        <div style={{
          padding: '12px 16px', borderRadius: 'var(--radius-sm)',
          background: 'rgba(239,68,68,0.1)', color: 'var(--danger)',
        }}>
          Error: {error.message}
        </div>
      </div>
    );
  }

  const company = data?.company;

  if (!company) {
    return (
      <div className="page container" style={{ paddingTop: 80 }}>
        <div className="card" style={{ padding: 40, textAlign: 'center', maxWidth: 480, margin: '0 auto' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🔍</div>
          <h2 style={{ fontWeight: 700, marginBottom: 8 }}>Company not found</h2>
          <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>
            No company with the slug <strong>{slug}</strong> exists on this platform.
          </p>
          <Link to="/" className="btn btn-ghost">Back to Home</Link>
        </div>
      </div>
    );
  }

  const admins = company.admins || [];
  const brandColor = company.brandColor || 'var(--primary)';

  return (
    <div className="page">
      {/* Hero Banner */}
      <div style={{
        background: brandColor,
        paddingTop: 60,
        paddingBottom: 0,
      }}>
        <div className="container" style={{ maxWidth: 760, paddingBottom: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap', paddingBottom: 28 }}>
            {/* Logo / avatar */}
            {company.logoUrl ? (
              <img
                src={company.logoUrl}
                alt={company.name}
                style={{
                  width: 88, height: 88, objectFit: 'contain',
                  background: 'rgba(255,255,255,0.95)',
                  borderRadius: 16, padding: 6,
                  border: '3px solid rgba(255,255,255,0.5)',
                  boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                  flexShrink: 0,
                }}
              />
            ) : (
              <div style={{
                width: 88, height: 88, borderRadius: 16, flexShrink: 0,
                background: 'rgba(255,255,255,0.25)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 900, fontSize: 40, color: '#fff',
                border: '3px solid rgba(255,255,255,0.4)',
              }}>
                {company.name[0].toUpperCase()}
              </div>
            )}

            <div style={{ flex: 1, minWidth: 200 }}>
              <h1 style={{ fontSize: 'var(--font-xl)', fontWeight: 900, color: '#fff', margin: 0 }}>
                {company.name}
              </h1>
              {company.tagline && (
                <div style={{ fontSize: 'var(--font-sm)', color: 'rgba(255,255,255,0.85)', marginTop: 6 }}>
                  {company.tagline}
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                {company.industry && (
                  <span style={{
                    fontSize: 12, color: 'rgba(255,255,255,0.9)',
                    background: 'rgba(255,255,255,0.2)',
                    padding: '3px 10px', borderRadius: 20,
                  }}>
                    {company.industry}
                  </span>
                )}
                {company.sizeBand && (
                  <span style={{
                    fontSize: 12, color: 'rgba(255,255,255,0.9)',
                    background: 'rgba(255,255,255,0.2)',
                    padding: '3px 10px', borderRadius: 20,
                  }}>
                    {company.sizeBand} employees
                  </span>
                )}
                <span style={{
                  fontSize: 12, color: 'rgba(255,255,255,0.7)',
                  fontFamily: 'monospace',
                }}>
                  {company.slug}.ledxo.com
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container" style={{ maxWidth: 760, paddingTop: 28, paddingBottom: 48 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 24, alignItems: 'start' }}>
          {/* Left column */}
          <div>
            {/* About */}
            {company.description && (
              <div className="card" style={{ padding: 24, marginBottom: 20 }}>
                <h2 style={{ fontWeight: 700, fontSize: 'var(--font-base)', marginBottom: 10 }}>About</h2>
                <p style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', lineHeight: 1.6 }}>
                  {company.description}
                </p>
              </div>
            )}

            {/* CTA */}
            <div className="card" style={{ padding: 24, marginBottom: 20 }}>
              <h2 style={{ fontWeight: 700, fontSize: 'var(--font-base)', marginBottom: 8 }}>
                Join this company's community
              </h2>
              <p style={{ fontSize: 'var(--font-sm)', color: 'var(--text-muted)', marginBottom: 16 }}>
                Explore groups, events and more created by {company.name} members.
              </p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Link
                  to={`/?company=${company.slug}`}
                  className="btn btn-primary"
                  style={{ background: brandColor, borderColor: brandColor }}
                >
                  Browse {company.name} Groups
                </Link>
                {currentUser ? null : (
                  <Link to="/?join=true" className="btn btn-ghost">
                    Sign up free
                  </Link>
                )}
              </div>
            </div>

            {/* Admins */}
            {admins.length > 0 && (
              <div className="card" style={{ padding: 24 }}>
                <h2 style={{ fontWeight: 700, fontSize: 'var(--font-base)', marginBottom: 14 }}>
                  Admins
                </h2>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {admins.map(admin => (
                    <Link
                      key={admin.id}
                      to={`/profile/${admin.id}`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 14px', borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border)', background: 'var(--surface2)',
                        textDecoration: 'none', color: 'var(--text)',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = brandColor}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                    >
                      <div className="avatar avatar-sm" style={{ background: admin.avatarColor || 'var(--primary)', flexShrink: 0 }}>
                        {(admin.name || '?')[0].toUpperCase()}
                      </div>
                      <span style={{ fontSize: 'var(--font-sm)', fontWeight: 600 }}>{admin.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right column */}
          <div>
            <div className="card" style={{ padding: 20, marginBottom: 16 }}>
              <h3 style={{ fontWeight: 700, fontSize: 'var(--font-sm)', marginBottom: 14, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Company Info
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {company.industry && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16 }}>🏭</span>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Industry</div>
                      <div style={{ fontSize: 'var(--font-sm)', fontWeight: 600 }}>{company.industry}</div>
                    </div>
                  </div>
                )}
                {company.sizeBand && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16 }}>👥</span>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Size</div>
                      <div style={{ fontSize: 'var(--font-sm)', fontWeight: 600 }}>{company.sizeBand} employees</div>
                    </div>
                  </div>
                )}
                {company.website && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16 }}>🌐</span>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Website</div>
                      <a
                        href={company.website}
                        target="_blank"
                        rel="noreferrer"
                        style={{ fontSize: 'var(--font-sm)', fontWeight: 600, color: 'var(--primary)' }}
                      >
                        Visit website ↗
                      </a>
                    </div>
                  </div>
                )}
                {company.plan && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 16 }}>✨</span>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Plan</div>
                      <div style={{ fontSize: 'var(--font-sm)', fontWeight: 600 }}>{company.plan}</div>
                    </div>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 16 }}>📅</span>
                  <div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Member since</div>
                    <div style={{ fontSize: 'var(--font-sm)', fontWeight: 600 }}>
                      {new Date(company.createdAt).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* External link */}
            <a
              href={`https://${company.slug}.ledxo.com`}
              target="_blank"
              rel="noreferrer"
              className="btn"
              style={{
                display: 'block', textAlign: 'center', width: '100%',
                background: brandColor, color: '#fff',
                border: `1px solid ${brandColor}`,
                padding: '10px 0', borderRadius: 'var(--radius-sm)',
                fontWeight: 600, fontSize: 'var(--font-sm)',
                textDecoration: 'none',
              }}
            >
              Open {company.name} Portal ↗
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
